import { useState } from 'react';

export default function Avatars() {
  const [activeSlide, setActiveSlide] = useState(0);

  const avatars = [
    {
      title: 'Professional Mom / Homeowner',
      headline: 'Safety. Light. Control.',
      microline: 'Child-safe ventilation. Energy savings. Peace of mind.',
      image: 'https://public.readdy.ai/ai/img_res/336e8ff24775ba925938dd1b61faa544.jpg',
      cta: 'See How It Works'
    },
    {
      title: 'The Athlete Mindset',
      headline: 'Discipline. Precision. No Runaround.',
      microline: 'Transparent process. Ironclad quotes. Respect for your time.',
      image: 'https://readdy.ai/api/search-image?query=Athletic%20disciplined%20professional%20in%20their%20thirties%20in%20modern%20minimalist%20home%20office%20with%20floor%20to%20ceiling%20new%20windows%20natural%20light%20focused%20confident%20expression%20precision%20mindset%20premium%20lifestyle%20photography%20cinematic%20clean%20aesthetic&width=1200&height=700&seq=avatar-athlete&orientation=landscape',
      cta: 'Get Your Quote'
    },
    {
      title: 'Small Business Owner',
      headline: 'Efficiency. Clarity. Better Space.',
      microline: 'Upgrade your café, studio, or office. Fast, clean, professional.',
      image: 'https://readdy.ai/api/search-image?query=Small%20business%20owner%20in%20contemporary%20yoga%20studio%20or%20cafe%20with%20beautiful%20new%20large%20windows%20abundant%20natural%20light%20clean%20modern%20space%20professional%20confident%20expression%20premium%20commercial%20photography%20cinematic%20aesthetic&width=1200&height=700&seq=avatar-business&orientation=landscape',
      cta: 'Start Your Project'
    }
  ];

  const nextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % avatars.length);
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev - 1 + avatars.length) % avatars.length);
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <section id="avatars" className="py-section bg-white">
      <div className="max-w-content mx-auto px-6">
        {/* Title */}
        <div className="text-center mb-8">
          <h2 className="text-4xl md:text-5xl font-heading font-semibold text-ink mb-5 tracking-tight">
            Built for people who value control.
          </h2>
          
          {/* Introduction Text */}
          <div className="max-w-3xl mx-auto mb-6">
            <p className="text-lg text-ink/70 leading-relaxed">
              Snapwindows was designed for homeowners who take pride in their home and want to make smart, confident decisions without pressure, confusion, or sales tactics.
            </p>
            <p className="text-lg text-ink/70 leading-relaxed mt-3">
              If you value clarity, quality, and staying in control of the process, you're in the right place.
            </p>
          </div>

          {/* Personas Content - Always Visible */}
          <div className="max-w-4xl mx-auto mb-12">
            {/* Section Header */}
            <div className="mb-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-calm/10 rounded-full mb-4">
                <div className="w-2 h-2 bg-calm rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-calm uppercase tracking-wide">
                  Who Snapwindows Is Built For
                </span>
              </div>
              <p className="text-lg text-ink/70">
                Snapwindows is built for homeowners who are:
              </p>
            </div>

            {/* Four Personas Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Persona 1 */}
              <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-border/50 hover:border-calm/30 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-calm to-[#52AB98] transform scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-top"></div>
                <div className="relative">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-calm to-[#52AB98] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-parent-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-ink mb-3">
                    Family-Focused Decision Makers
                  </h3>
                  <p className="text-base text-ink/70 leading-relaxed">
                    You care about safety, comfort, and energy efficiency and you want solutions that make your home better for the people who live in it.
                  </p>
                </div>
              </div>

              {/* Persona 2 */}
              <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-border/50 hover:border-calm/30 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-calm to-[#52AB98] transform scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-top"></div>
                <div className="relative">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-calm to-[#52AB98] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-shield-check-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-ink mb-3">
                    Homeowners Burned by the Process
                  </h3>
                  <p className="text-base text-ink/70 leading-relaxed">
                    You've dealt with unclear pricing, shifting timelines, poor communication, or surprises that showed up after decisions were already made. You want a process where expectations, pricing, and next steps are clear from the start.
                  </p>
                </div>
              </div>

              {/* Persona 3 */}
              <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-border/50 hover:border-calm/30 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-calm to-[#52AB98] transform scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-top"></div>
                <div className="relative">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-calm to-[#52AB98] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-home-heart-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-ink mb-3">
                    Proud of Their Home
                  </h3>
                  <p className="text-base text-ink/70 leading-relaxed">
                    You see your home as a reflection of your standards, and you want improvements that enhance its beauty and value.
                  </p>
                </div>
              </div>

              {/* Persona 4 */}
              <div className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-border/50 hover:border-calm/30 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-calm to-[#52AB98] transform scale-y-0 group-hover:scale-y-100 transition-transform duration-500 origin-top"></div>
                <div className="relative">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-calm to-[#52AB98] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-line-chart-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-heading font-semibold text-ink mb-3">
                    Quality & Investment-Minded
                  </h3>
                  <p className="text-base text-ink/70 leading-relaxed">
                    You think long-term. You value quality materials, professional installation, and upgrades that pay off over time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Slider */}
        <div className="relative max-w-6xl mx-auto">
          {/* Navigation Dots */}
          <div className="flex justify-center gap-3 mt-8">
            {avatars.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveSlide(index)}
                className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                  index === activeSlide
                    ? 'bg-calm w-8'
                    : 'bg-calm/30 hover:bg-calm/50'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}